// client/src/pages/actions/EditAction.js
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { apiFetch } from "../../api/api";
import QRCode from "qrcode";
import { toast } from "react-toastify";

const TYPES = ["events", "games", "tracking", "access"];

const pageLayoutStyle = {
  display: 'flex',
  gap: '20px',
  alignItems: 'flex-start'
};

const leftColumnStyle = {
  flex: 1,
  minWidth: 0
};

const rightColumnStyle = {
  width: '320px',
  flexShrink: 0
};

const cardPanelStyle = {
  background: "#eef2f7",
  padding: "16px 20px"
};

const whiteInnerCardStyle = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  padding: 16
};

export default function EditAction() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [type, setType] = useState(TYPES[0]);
  const [status, setStatus] = useState("active");

  const [datasetId, setDatasetId] = useState("");
  const [datasetName, setDatasetName] = useState("");

  const [landingPageId, setLandingPageId] = useState("");
  const [cardDesignId, setCardDesignId] = useState("");

  const [landingPages, setLandingPages] = useState([]);
  const [cards, setCards] = useState([]);

  const [printerGroups, setPrinterGroups] = useState([]);
  const [printerGroupId, setPrinterGroupId] = useState("");

  const [groupPrinters, setGroupPrinters] = useState([]);
  const [selectedPrinters, setSelectedPrinters] = useState([]);
  const [printMode, setPrintMode] = useState("gallery");

  const [qrUrl, setQrUrl] = useState("");
  const [qrImg, setQrImg] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const printersDisabled = printMode === "none";
  const [landscapeCardDesignId, setLandscapeCardDesignId] = useState("");
  const [portraitCardDesignId, setPortraitCardDesignId] = useState("");
  const [copied, setCopied] = useState(false);

  const rowStyle = useMemo(() => ({
    display: "grid",
    gridTemplateColumns: "220px minmax(0,1fr)",
    gap: 12,
    alignItems: "center",
  }), []);

  const controlStyle = { width: "100%" };

  // QR
  useEffect(() => {
    const url = `${window.location.origin}/lp/${id}`;
    setQrUrl(url);
    QRCode.toDataURL(url, { width: 280 }).then(setQrImg);
  }, [id]);

  // Load base data
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        const act = await apiFetch(`/actions/getById/${id}`);
        console.log("act:", act)
        setName(act.name || "");
        setType(act.type || TYPES[0]);
        setStatus(act.status || "inactive");

        setLandingPageId(act.landingPageId || "");
        setCardDesignId(act.cardDesignId || "");
        setLandscapeCardDesignId(
          act.landscapeCardDesignId || ""
        );
        setPortraitCardDesignId(
          act.portraitCardDesignId || ""
        );

        setPrinterGroupId(act.printerGroupId || "");
        setPrintMode(act.printMode || "gallery");

        const initialPrinters = act.printerNames || [];
        setSelectedPrinters(initialPrinters);

        const dsId = act.datasetId;
        setDatasetId(dsId);

        if (dsId) {
          const ds = await apiFetch(`/datasets/get/${dsId}`);
          setDatasetName(ds?.name || dsId);
        }

        const groups = await apiFetch("/api/printmanager/printergroups");
        setPrinterGroups(groups || []);

        const lp = await apiFetch("/landingpages/getAll", { method: 'POST' });
        setLandingPages(lp || []);

        const cd = await apiFetch("/cards/getAll");
        setCards(cd.items || []);

      } catch (err) {
        toast.error("Failed to load Action");
        navigate("/datasets");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // 🔥 Load printers when group changes
  useEffect(() => {
    if (!printerGroupId) {
      setGroupPrinters([]);
      return;
    }

    (async () => {
      try {
        const res = await apiFetch(`/pm/printer-groups/${printerGroupId}/printers`);
        setGroupPrinters(res || []);
      } catch {
        toast.error("Failed to load printers in group");
      }
    })();
  }, [printerGroupId]);

  const togglePrinter = (key) => {
    setSelectedPrinters((prev) =>
      prev.includes(key)
        ? prev.filter((p) => p !== key)
        : [...prev, key]
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      await apiFetch(`/actions/update/${id}`, {
        method: "POST",
        body: {
          name,
          type,
          status,
          landingPageId,
          cardDesignId,
          landscapeCardDesignId,
          portraitCardDesignId,
          datasetId,
          printerGroupId,
          printerNames: selectedPrinters,
          printMode,
        },
      });

      toast.success("Action updated");
      navigate(-1);

    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div>Loading…</div>;



  return (
    <div style={{ marginLeft: 20 }}>

      <div className="datatable-header-row">
        <h2>Edit Action</h2>
        <div>
          <button className="btn-blue" onClick={() => navigate(-1)}>Return</button>&nbsp;&nbsp;&nbsp;
          <button className="btn-green" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div><br/>

      <div style={pageLayoutStyle}>

        {/* LEFT COLUMN */}

        <div style={leftColumnStyle}>

          <div
            className="detail-card"
            style={cardPanelStyle}
          >

            <div style={rowStyle}>
              <label><b>Project Name:</b> {datasetName}</label>
            </div>

            <br />

            <div style={rowStyle}>
              <label>Action Name</label>
              <input
                value={name}
                onChange={e => setName(e.target.value)}
              />
            </div>

            <div style={rowStyle}>
              <label>Type</label>
              <select
                value={type}
                onChange={e => setType(e.target.value)}
              >
                {TYPES.map(t => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </div>

            <div style={rowStyle}>
              <label>Landing Page</label>
              <select
                value={landingPageId}
                onChange={e => setLandingPageId(e.target.value)}
              >
                <option value="">-- Select --</option>

                {landingPages.map(lp => (
                  <option key={lp._id} value={lp._id}>
                    {lp.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={rowStyle}>
              <label>Landscape Card</label>

              <select
                value={landscapeCardDesignId}
                onChange={e => setLandscapeCardDesignId(e.target.value)}
              >
                <option value="">-- Select --</option>

                {cards.map(c => (
                  <option key={c._id} value={c._id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={rowStyle}>
              <label>Portrait Card</label>

              <select
                value={portraitCardDesignId}
                onChange={e => setPortraitCardDesignId(e.target.value)}
              >
                <option value="">-- Select --</option>

                {cards.map(c => (
                  <option key={c._id} value={c._id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={rowStyle}>
              <label>Status</label>

              <select
                value={status}
                onChange={e => setStatus(e.target.value)}
              >
                <option value="active">active</option>
                <option value="inactive">inactive</option>
              </select>
            </div>

            <div style={{ opacity: printersDisabled ? 0.5 : 1 }}>
              <div style={rowStyle}>
                <label>Printer Group</label>

                <select
                  value={printerGroupId}
                  disabled={printersDisabled}
                  onChange={e => setPrinterGroupId(e.target.value)}
                >
                  <option value="">-- Select --</option>

                  {printerGroups.map(g => (
                    <option key={g._id} value={g._id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={rowStyle}>
              <label>Printers in Group</label>

              <div className="printer-checkbox-grid">

                {groupPrinters.length === 0 && (
                  <div style={{ color: "#666" }}>
                    No printers assigned to this group
                  </div>
                )}

                {groupPrinters.map(p => (
                  <label
                    key={p.printerKey}
                    className="printer-checkbox"
                  >
                    <input
                      type="checkbox"
                      checked={selectedPrinters.includes(p.printerKey)}
                      onChange={() =>
                        togglePrinter(p.printerKey)
                      }
                    />

                    {p.name || p.printerKey}
                    <br />
                  </label>
                ))}

              </div>
            </div>

            <div style={rowStyle}>
              <label>Print Mode</label>

              <select
                value={printMode}
                onChange={(e) =>
                  setPrintMode(e.target.value)
                }
                style={{ width: "100%" }}
              >
                <option value="gallery">
                  Gallery (manual print)
                </option>

                <option value="immediate">
                  Immediate Print
                </option>

                <option value="none">
                  No Print
                </option>
              </select>
            </div>

          </div>

        </div>

        {/* RIGHT COLUMN */}

        <div style={rightColumnStyle}>

          <div
            className="detail-card"
            style={cardPanelStyle}
          >

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px'
            }}
            >
            <h3 style={{ margin: 0 }}>
              Action URL
            </h3>

            <button
              className="btn-blue"
              style={{
                padding: '4px 10px',
                fontSize: '.8rem'
              }}
              onClick={() => {

                navigator.clipboard.writeText(qrUrl);

                setCopied(true);

                setTimeout(() => {
                  setCopied(false);
                }, 2000);

              }}
            >
              {copied ? '✓ Copied' : 'Copy'}
            </button>

            </div>

            <div style={whiteInnerCardStyle}>

              <div
                style={{
                  wordBreak: 'break-all',
                  fontSize: '.85rem',
                  marginBottom: '20px'
                }}
              >
                {qrUrl}
              </div>

              {qrImg && (
                <div style={{ textAlign: 'center' }}>
                <img
                  src={qrImg}
                  alt="QR"
                  style={{
                    width: '250px',
                    maxWidth: '100%'
                  }}
                />
                </div>
            )}
            </div>

          </div>

        </div>

      </div>

    </div>
  );
}
